// src/App/Components/VendorGamesComponent.js
import React, { Component } from "react";
import { Alert } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { ChevronLeft, ChevronRight, Play } from "lucide-react";
import styled from 'styled-components';
import { getUID, sendNotfication } from "../../../Helper";

/* ============= Styled Components ============= */

const FullScreenContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999;
  background-color: #1b2336;
`;

const FullScreenIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  display: ${(props) => (props.loading ? 'none' : 'block')};
`;

const BackButton = styled.button`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1000;
  background: none;
  border: none;
  color: #000;
  font-size: 24px;
  cursor: pointer;
`;

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 12px;
  
  @media (max-width: 767px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const SkeletonLoader = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(190, 190, 190, 0.2) 25%,
    rgba(129, 129, 129, 0.24) 37%,
    rgba(190, 190, 190, 0.2) 63%
  );
  background-size: 400% 100%;
  animation: skeleton-loading 1.4s ease infinite;
  border-radius: 8px;

  @keyframes skeleton-loading {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`;

const HeaderContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 12px 0;
`;

const HeaderContent = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const IconBg = styled.div`
  padding: 6px 8px;
  background-color: #03030F;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  &:hover {
    background-color: #4a5354;
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
`;

const ScrollContainer = styled.div`
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  gap: 11px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const GameOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(5px);
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s ease;
`;


const GameCard = styled.div`
  min-width: 142px;
  height: 195px;
  border-radius: 8px;
  position: relative;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transition: transform 0.2s ease;

  &:hover {
    ${GameOverlay} {
      opacity: 1;
    }
    transform: translateY(-5px);
  }

  @media (max-width: 768px) {
    min-width: 115px;
    height: 160px;
  }
`;


const GameImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
`;

const PlayIconContainer = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PlayIcon = styled(Play)`
  color: #fff;
  font-size: 24px;
`;

const TopTitle = styled.div`
  color: white;
  font-size: 16px;
  font-weight: bold;
  margin-left: 4px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  gap: 10px;
`;

const PaginationButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 12px;
  background-color: ${props => props.disabled ? '#3a3a3a' : '#4a4a4a'};
  color: ${props => props.disabled ? '#777' : 'white'};
  border: none;
  border-radius: 4px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  
  &:hover {
    background-color: ${props => props.disabled ? '#3a3a3a' : '#5a5a5a'};
  }
`;

const PaginationInfo = styled.div`
  color: white;
  font-size: 14px;
`;

const SpinnerStyled = styled.div`
  width: ${props => props.size === "sm" ? "16px" : "24px"};
  height: ${props => props.size === "sm" ? "16px" : "24px"};
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

/* ============= Cache Helpers ============= */

const CACHE_PREFIX = "jsgames_cache_v1_";
const CACHE_TTL_MS = 1000 * 60 * 60 * 12; // 12 hours

function getCache(key) {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.ts) return null;
    if (Date.now() - parsed.ts > (parsed.ttl || CACHE_TTL_MS)) {
      localStorage.removeItem(CACHE_PREFIX + key);
      return null;
    }
    return parsed.data;
  } catch (e) {
    return null;
  }
}

function setCache(key, data, ttl = CACHE_TTL_MS) {
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ ts: Date.now(), ttl, data }));
  } catch (e) {
    // ignore localStorage errors
  }
}

/* ============= Main Component ============= */

class VendorGamesComponent extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      games: [],
      loading: true,
      currentPage: 1,
      totalPages: 1,
      showMore: false,
      loadedImages: new Set(),
      showIframe: false,
      iframeUrl: '',
      iframeLoading: true,
      message: null,
      isMobileView: window.innerWidth <= 768
    };

    this.scrollRef = React.createRef();
    this._isMounted = false;
  }

  componentDidMount() {
    this._isMounted = true;
    window.addEventListener('resize', this.handleResize);
    this.fetchGames(1);
  }

  componentWillUnmount() {
    this._isMounted = false;
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    if (this._isMounted) {
      this.setState({ isMobileView: width <= 768 });
    }
  };

  fetchGames = async (page = 1, perPage = 20, force = false) => {
    const { vendorId } = this.props;
    const cacheKey = `${vendorId}_p${page}_per${perPage}`;
    
    if (!force) {
      const cached = getCache(cacheKey);
      if (cached) {
        this.setState({
          games: cached.games || [],
          currentPage: cached.pagination?.current_page || page,
          totalPages: cached.pagination?.total_pages || 1,
          loading: false
        });
        return;
      }
    }

    this.setState({ loading: true });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `https://api.ibitplay.com/jsGames/games?vendor=${vendorId}&page=${page}&per_page=${perPage}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error("Network response was not ok");
      
      const result = await response.json();

      if (result.success && result.payload && result.payload.games) {
        const games = result.payload.games;
        const pagination = result.payload.pagination || {
          current_page: page,
          per_page: perPage,
          total: games.length,
          total_pages: Math.ceil(games.length / perPage)
        };

        setCache(cacheKey, { games, pagination });

        if (this._isMounted) {
          this.setState({
            games,
            currentPage: pagination.current_page,
            totalPages: pagination.total_pages,
            loading: false
          });

          // Preload first few images
          games.slice(0, 6).forEach(g => {
            if (g && g.game_icon) {
              const img = new Image();
              img.src = g.game_icon;
            }
          });
        }
      }
    } catch (err) {
      console.error(`Error fetching ${vendorId} games:`, err.message);
      if (this._isMounted) {
        this.setState({ loading: false });
      }
    }
  };

  handleGameLaunch = async (game) => {
    const isLogged = localStorage.getItem('logged') === 'true';
    if (!isLogged) {
      sendNotfication('Please login to play', 'error');
      return;
    }

    this.setState({ loading: true, message: null });

    try {
      const credits = localStorage.getItem('credit') || '0';
      const coins = localStorage.getItem('coin') || 'USDT';

      if (coins !== 'USDT' && coins !== 'INR' && coins !== 'BDT') {
        sendNotfication("Please Swap Into USDT To Play", "error");
        this.setState({ loading: false });
        return;
      }

      const requestPayload = {
        game_uid: game.game_uid,
        user_id: getUID(),
        credit_amount: credits,
        currency_code: coins,
        language: "en",
      };

      const token = localStorage.getItem('token');
      const response = await fetch('https://api.ibitplay.com/jsGamesv2/launch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestPayload),
      });

      if (!response.ok) {
        sendNotfication("Game under maintenance", "error");
      }

      const result = await response.json();

      if (result.success && result.data.game_launch_url) {
        this.setState({
          iframeUrl: result.data.game_launch_url,
          showIframe: true,
          iframeLoading: true,
        });
      }
    } catch (err) {
      this.setState({ message: { type: 'error', text: err.message } });
    } finally {
      this.setState({ loading: false });
      setTimeout(() => this.setState({ message: null }), 5000);
    }
  };

  handleImageLoad = (gameId) => {
    const id = String(gameId);
    this.setState(prev => {
      const set = new Set(prev.loadedImages);
      set.add(id);
      return { loadedImages: set };
    });
  };

  handleToggleShowMore = () => {
    this.setState(prev => ({ showMore: !prev.showMore }));
  };

  handleScroll = (direction) => {
    if (this.scrollRef && this.scrollRef.current) {
      this.scrollRef.current.scrollBy({ 
        left: direction === 'left' ? -400 : 400, 
        behavior: 'smooth' 
      });
    }
  };

  handlePagination = (direction) => {
    const { currentPage, totalPages, loading } = this.state;
    const targetPage = direction === 'next' ? currentPage + 1 : currentPage - 1;

    if (targetPage < 1 || targetPage > totalPages || loading) return;

    this.fetchGames(targetPage, 20, true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  closeIframe = () => {
    this.setState({
      showIframe: false,
      iframeUrl: '',
      iframeLoading: true
    });
  };

  render() {
    const { 
      games, 
      loading, 
      currentPage, 
      totalPages, 
      showMore, 
      loadedImages,
      showIframe,
      iframeUrl,
      iframeLoading,
      message,
      isMobileView
    } = this.state;

    const { displayName } = this.props;

    // Show iframe if game launched
    if (showIframe) {
      const imageStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '250px',
        animation: 'fadeinout 2s infinite',
      };
      const animationStyle = `
        @keyframes fadeinout {
          0%, 100% { opacity: 0; }
          50% { opacity: 1; }
        }
      `;

      return (
        <FullScreenContainer>
          <style>{animationStyle}</style>
          {iframeLoading && (
            <img src="./assets/betlover365.png" alt="Loading" style={imageStyle} />
          )}
          <FullScreenIframe 
            src={iframeUrl} 
            title="Game Iframe" 
            onLoad={() => this.setState({ iframeLoading: false })} 
            loading={iframeLoading} 
          />
          <BackButton onClick={this.closeIframe}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </BackButton>
        </FullScreenContainer>
      );
    }

    const displayGames = isMobileView ? games : (showMore ? games : games.slice(0, 40));

    return (
      <div style={{ marginTop: "6px" }}>
        <HeaderContainer>
          <TopTitle>{displayName}</TopTitle>
          <HeaderContent>
            <div onClick={this.handleToggleShowMore}>
              <IconBg>
                <div style={{ fontWeight: "bold" }}>
                  {showMore ? "Show Less" : "Show More"}
                </div>
              </IconBg>
            </div>

            {!showMore && (
              <div style={{ display: "flex", gap: '4px' }}>
                <IconBg onClick={() => this.handleScroll('left')}>
                  <ChevronLeft size={16} />
                </IconBg>
                <IconBg onClick={() => this.handleScroll('right')}>
                  <ChevronRight size={16} />
                </IconBg>
              </div>
            )}
          </HeaderContent>
        </HeaderContainer>

        {loading && games.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'white', padding: '20px' }}>
            <SpinnerStyled />
          </div>
        ) : !showMore ? (
          <CarouselContainer>
            <ScrollContainer ref={this.scrollRef}>
              {displayGames?.map((game, idx) => {
                const gid = String(game.id ?? `${game.game_uid}-${idx}`);
                return (
                  <GameCard key={gid} onClick={() => this.handleGameLaunch(game)}>
                    {!loadedImages.has(gid) && <SkeletonLoader />}
                    <GameImage
                      data-gameid={gid}
                      src={game.game_icon || ''}
                      alt={`${displayName} ${idx}`}
                      loading="lazy"
                      onLoad={() => this.handleImageLoad(gid)}
                      onError={() => this.handleImageLoad(gid)}
                      style={{ display: 'block' }}
                    />
                    <GameOverlay>
                      <PlayIconContainer>
                        <PlayIcon />
                      </PlayIconContainer>
                    </GameOverlay>
                  </GameCard>
                );
              })}
            </ScrollContainer>
          </CarouselContainer>
        ) : (
          <>
            <GridWrapper>
              {displayGames?.map((game, idx) => {
                const gid = String(game.id ?? `${game.game_uid}-grid-${idx}`);
                return (
                  <GameCard key={gid} onClick={() => this.handleGameLaunch(game)}>
                    {!loadedImages.has(gid) && <SkeletonLoader />}
                    <GameImage
                      data-gameid={gid}
                      src={game.game_icon || ''}
                      alt={`${displayName} ${idx}`}
                      loading="lazy"
                      onLoad={() => this.handleImageLoad(gid)}
                      onError={() => this.handleImageLoad(gid)}
                      style={{ display: 'block' }}
                    />
                    <GameOverlay>
                      <PlayIconContainer>
                        <PlayIcon />
                      </PlayIconContainer>
                    </GameOverlay>
                  </GameCard>
                );
              })}
            </GridWrapper>

            <PaginationContainer>
              <PaginationButton 
                onClick={() => this.handlePagination('prev')} 
                disabled={currentPage <= 1 || loading}
              >
                <ChevronLeft size={16} /> Previous
              </PaginationButton>

              <PaginationInfo>
                Page {currentPage} of {totalPages}
              </PaginationInfo>

              <PaginationButton 
                onClick={() => this.handlePagination('next')} 
                disabled={currentPage >= totalPages || loading}
              >
                Next <ChevronRight size={16} />
              </PaginationButton>

              {loading && <SpinnerStyled size="sm" />}
            </PaginationContainer>
          </>
        )}

        {message && (
          <Alert 
            variant={message.type === 'error' ? 'danger' : 'success'} 
            style={{ 
              position: 'fixed', 
              bottom: 20, 
              left: '50%', 
              transform: 'translateX(-50%)', 
              zIndex: 9999 
            }}
          >
            {message.text}
          </Alert>
        )}
      </div>
    );
  }
}

export default VendorGamesComponent;